import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { errorsAPI } from '../api';
import useWebSocket from '../hooks/useWebSocket';
import {
  AlertTriangle, AlertCircle, Info, ShieldAlert,
  Search, Filter, ChevronRight, X, User, Package,
  Scissors, Copy, TrendingUp, ChevronsRight, CheckCircle,
  Clock, Loader2, Terminal,
} from 'lucide-react';

const SEVERITY_META = {
  critical: { color: '#991B1B', bg: 'rgba(153,27,27,0.08)', Icon: AlertTriangle, label: 'Critical' },
  high:     { color: '#B45309', bg: 'rgba(180,83,9,0.08)',  Icon: AlertCircle,   label: 'High' },
  medium:   { color: '#D4AF37', bg: 'rgba(212,175,55,0.12)', Icon: AlertCircle,  label: 'Medium' },
  low:      { color: '#595959', bg: 'rgba(89,89,89,0.06)',   Icon: Info,         label: 'Low' },
};

const STATUS_META = {
  open:          { color: '#991B1B', label: 'Open' },
  investigating: { color: '#D4AF37', label: 'Investigating' },
  resolved:      { color: '#166534', label: 'Resolved' },
};

const FILTERS = [
  { id: 'all',      label: 'All' },
  { id: 'critical', label: 'Critical', severity: true },
  { id: 'high',     label: 'High',     severity: true },
  { id: 'medium',   label: 'Medium',   severity: true },
  { id: 'low',      label: 'Low',      severity: true },
  { id: 'resolved', label: 'Resolved' },
];

const CATEGORY_LABEL = {
  auth: 'Auth', order: 'Order', payment: 'Payment', '3dlook': '3DLOOK',
  email: 'Email', sms: 'SMS', system: 'System',
};

const AdminErrors = () => {
  const [filter, setFilter] = useState('all');
  const [category, setCategory] = useState(null);
  const [summary, setSummary] = useState({ total: 0, open: 0, critical: 0, resolved: 0 });
  const [errors, setErrors] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('errors'); // 'errors' | 'reports'
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [criticalAlert, setCriticalAlert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        errorsAPI.summary(),
        errorsAPI.list(filter, category),
      ]);
      setSummary(s); setErrors(list);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [filter, category]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view === 'reports' && !reports) {
      errorsAPI.reports(14).then(setReports).catch(() => {});
    }
  }, [view, reports]);

  // Real-time critical error alerts from WebSocket
  useWebSocket(useCallback((msg) => {
    if (msg && msg.type === 'critical_error') {
      setCriticalAlert({ error_id: msg.error_id, type: msg.error_type, message: msg.message, at: msg.at });
      load();
      // Auto-dismiss the banner after 12 s if user doesn't act
      setTimeout(() => setCriticalAlert(prev => prev && prev.error_id === msg.error_id ? null : prev), 12000);
    }
  }, [load]));

  const filteredList = useMemo(() => {
    if (!search) return errors;
    const s = search.toLowerCase();
    return errors.filter(e =>
      (e.type || '').toLowerCase().includes(s) ||
      (e.message || '').toLowerCase().includes(s) ||
      (e.category || '').toLowerCase().includes(s)
    );
  }, [errors, search]);

  const openDetail = async (err) => {
    setSelected(err);
    setDetail(null);
    try { setDetail(await errorsAPI.detail(err.id)); } catch {/* silent */}
  };

  const changeStatus = async (newStatus) => {
    if (!detail || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const updated = await errorsAPI.updateStatus(detail.id, newStatus);
      setDetail(updated);
      load();
    } catch (e) {
      alert(e.message || 'Could not update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const StatCard = ({ label, value, tint, testId }) => (
    <div data-testid={testId} className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
      <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>{label}</p>
      <p className="text-2xl mt-1" style={{ fontFamily: 'Playfair Display, serif', color: tint || '#0F0F0F' }}>
        {value}
      </p>
    </div>
  );

  return (
    <div data-testid="admin-errors" className="space-y-3">
      {/* Real-time critical banner */}
      {criticalAlert && (
        <div data-testid="critical-alert-banner"
          className="border-l-4 p-3 flex items-start justify-between animate-pulse"
          style={{ borderColor: '#991B1B', backgroundColor: 'rgba(153,27,27,0.08)' }}>
          <div className="flex items-start gap-2 min-w-0">
            <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#991B1B' }} strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-[11px] font-medium" style={{ color: '#991B1B' }}>Critical: {criticalAlert.type}</p>
              <p className="text-[10px] truncate" style={{ color: '#595959' }}>{criticalAlert.message}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <button data-testid="critical-alert-view"
              onClick={() => { openDetail({ id: criticalAlert.error_id, type: criticalAlert.type }); setCriticalAlert(null); }}
              className="text-[10px] px-2 py-1 border" style={{ borderColor: '#991B1B', color: '#991B1B' }}>
              View
            </button>
            <button data-testid="critical-alert-dismiss" onClick={() => setCriticalAlert(null)}>
              <X className="w-4 h-4" style={{ color: '#991B1B' }} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2">
        <StatCard testId="err-stat-total" label="Total" value={summary.total} />
        <StatCard testId="err-stat-open" label="Open" value={summary.open} tint="#B45309" />
        <StatCard testId="err-stat-critical" label="Critical" value={summary.critical} tint="#991B1B" />
        <StatCard testId="err-stat-resolved" label="Resolved" value={summary.resolved} tint="#166534" />
      </div>

      {/* View toggle */}
      <div className="flex gap-1">
        <button
          data-testid="err-view-list"
          onClick={() => setView('errors')}
          className="flex-1 py-2 text-[10px] tracking-[0.1em] uppercase border"
          style={{
            backgroundColor: view === 'errors' ? '#0F0F0F' : '#FFF',
            color: view === 'errors' ? '#FFF' : '#595959',
            borderColor: view === 'errors' ? '#0F0F0F' : '#E5E5E5',
          }}>
          <ShieldAlert className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Errors
        </button>
        <button
          data-testid="err-view-reports"
          onClick={() => setView('reports')}
          className="flex-1 py-2 text-[10px] tracking-[0.1em] uppercase border"
          style={{
            backgroundColor: view === 'reports' ? '#0F0F0F' : '#FFF',
            color: view === 'reports' ? '#FFF' : '#595959',
            borderColor: view === 'reports' ? '#0F0F0F' : '#E5E5E5',
          }}>
          <TrendingUp className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Reports
        </button>
      </div>

      {view === 'errors' && (
        <>
          {/* Severity + category filters */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            {FILTERS.map(f => (
              <button key={f.id} data-testid={`err-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className="flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.05em] uppercase border whitespace-nowrap"
                style={{
                  backgroundColor: filter === f.id ? '#0F0F0F' : '#FFF',
                  color: filter === f.id ? '#FFF' : '#595959',
                  borderColor: filter === f.id ? '#0F0F0F' : '#E5E5E5',
                }}>
                <Filter className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> {f.label}
              </button>
            ))}
          </div>

          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            <button data-testid="err-category-all"
              onClick={() => setCategory(null)}
              className="flex-shrink-0 px-2.5 py-1 text-[9px] tracking-[0.08em] uppercase border"
              style={{
                backgroundColor: !category ? '#D4AF37' : '#FFF',
                color: !category ? '#FFF' : '#595959',
                borderColor: !category ? '#D4AF37' : '#E5E5E5',
              }}>All Categories</button>
            {Object.entries(CATEGORY_LABEL).map(([id, label]) => (
              <button key={id} data-testid={`err-category-${id}`}
                onClick={() => setCategory(id === category ? null : id)}
                className="flex-shrink-0 px-2.5 py-1 text-[9px] tracking-[0.08em] uppercase border"
                style={{
                  backgroundColor: category === id ? '#D4AF37' : '#FFF',
                  color: category === id ? '#FFF' : '#595959',
                  borderColor: category === id ? '#D4AF37' : '#E5E5E5',
                }}>{label}</button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
            <input data-testid="err-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search errors..."
              className="w-full pl-9 pr-3 py-2 text-[12px] bg-white border focus:outline-none"
              style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            </div>
          )}
          {!loading && filteredList.length === 0 && (
            <div className="text-center py-10">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" style={{ color: '#166534' }} strokeWidth={1.5} />
              <p className="text-[12px]" style={{ color: '#595959' }}>No errors match this filter</p>
            </div>
          )}

          {filteredList.map(err => {
            const sev = SEVERITY_META[err.severity] || SEVERITY_META.low;
            const status = STATUS_META[err.status] || STATUS_META.open;
            const Icon = sev.Icon;
            return (
              <button key={err.id}
                data-testid={`err-card-${err.id}`}
                onClick={() => openDetail(err)}
                className="w-full bg-white border p-3 text-left transition-colors hover:bg-black/[0.02]"
                style={{ borderColor: '#E5E5E5', borderLeft: `3px solid ${sev.color}` }}>
                <div className="flex items-start justify-between mb-1.5 gap-2">
                  <div className="flex items-start gap-1.5 min-w-0 flex-1">
                    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: sev.color }} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className="px-1.5 py-px text-[9px] tracking-[0.05em] uppercase font-medium"
                          style={{ backgroundColor: sev.bg, color: sev.color }}>{sev.label}</span>
                        <span className="px-1.5 py-px text-[9px] tracking-[0.05em] uppercase"
                          style={{ backgroundColor: `${status.color}18`, color: status.color }}>{status.label}</span>
                        {err.count > 1 && (
                          <span className="px-1.5 py-px text-[9px] tracking-[0.05em]" style={{ backgroundColor: '#F0F0F0', color: '#595959' }}>
                            ×{err.count}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] font-medium truncate" style={{ color: '#0F0F0F' }}>{err.type}</p>
                      <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: '#595959' }}>{err.message}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-2 text-[10px]" style={{ color: '#595959' }}>
                  <Clock className="w-3 h-3" strokeWidth={1.5} />
                  <span>{err.last_seen ? new Date(err.last_seen).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                  {err.category && <><span>·</span><span className="uppercase tracking-[0.05em]">{err.category}</span></>}
                </div>
              </button>
            );
          })}
        </>
      )}

      {view === 'reports' && (
        <div data-testid="err-reports" className="space-y-3">
          {!reports && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            </div>
          )}
          {reports && (
            <>
              {/* Top error types */}
              <div className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#0F0F0F' }}>
                  Top Error Types <span className="text-[9px] normal-case" style={{ color: '#595959' }}>(last {reports.days}d)</span>
                </p>
                {reports.top_types.length === 0 && (
                  <p className="text-[11px]" style={{ color: '#595959' }}>No errors recorded</p>
                )}
                {reports.top_types.map((t, i) => {
                  const max = reports.top_types[0]?.count || 1;
                  const pct = Math.round((t.count / max) * 100);
                  return (
                    <div key={i} className="mb-2 last:mb-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[11px] truncate" style={{ color: '#0F0F0F' }}>{t.type}</p>
                        <p className="text-[11px] font-medium" style={{ color: t.critical > 0 ? '#991B1B' : '#0F0F0F' }}>{t.count}</p>
                      </div>
                      <div className="h-1 w-full" style={{ backgroundColor: '#EAEAEA' }}>
                        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: t.critical > 0 ? '#991B1B' : '#D4AF37' }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Errors by day (mini bar chart) */}
              <div className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#0F0F0F' }}>Errors by Day</p>
                {reports.by_day.length === 0 && (
                  <p className="text-[11px]" style={{ color: '#595959' }}>No data yet</p>
                )}
                {reports.by_day.length > 0 && (() => {
                  const max = Math.max(...reports.by_day.map(d => d.total)) || 1;
                  return (
                    <div className="flex items-end gap-1 h-24">
                      {reports.by_day.map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col justify-end items-center gap-1"
                          title={`${d.date}: ${d.total} (${d.critical} critical)`}>
                          <div className="w-full relative" style={{ height: `${(d.total / max) * 100}%`, minHeight: 4 }}>
                            <div className="absolute inset-0" style={{ backgroundColor: '#D4AF37' }} />
                            {d.critical > 0 && (
                              <div className="absolute bottom-0 left-0 right-0"
                                style={{ height: `${(d.critical / d.total) * 100}%`, backgroundColor: '#991B1B' }} />
                            )}
                          </div>
                          <p className="text-[8px]" style={{ color: '#595959' }}>{d.date.slice(-2)}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Critical trend */}
              <div className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
                <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#991B1B' }}>Critical Error Trends (Open)</p>
                {reports.critical_trend.length === 0 && (
                  <p className="text-[11px]" style={{ color: '#166534' }}>✓ No critical errors currently open</p>
                )}
                {reports.critical_trend.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: '#F0F0F0' }}>
                    <p className="text-[11px] uppercase tracking-[0.05em]" style={{ color: '#0F0F0F' }}>{CATEGORY_LABEL[c.category] || c.category}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px]" style={{ color: '#595959' }}>{c.distinct} distinct</span>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'rgba(153,27,27,0.1)', color: '#991B1B' }}>
                        {c.occurrences}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div data-testid="err-detail-modal" className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center"
          style={{ backgroundColor: 'rgba(15,15,15,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => { setSelected(null); setDetail(null); }}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg h-[92vh] sm:h-[88vh] bg-white flex flex-col shadow-2xl overflow-hidden"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E5E5E5' }}>
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.15em] uppercase" style={{ color: '#D4AF37' }}>Error Record</p>
                <h2 className="text-base truncate" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                  {(detail || selected).type}
                </h2>
              </div>
              <button data-testid="err-detail-close" onClick={() => { setSelected(null); setDetail(null); }} className="p-2 -mr-2">
                <X className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {!detail && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                </div>
              )}
              {detail && (() => {
                const sev = SEVERITY_META[detail.severity] || SEVERITY_META.low;
                const status = STATUS_META[detail.status] || STATUS_META.open;
                return (
                  <>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-2 py-1 text-[10px] tracking-[0.08em] uppercase font-medium"
                        style={{ backgroundColor: sev.bg, color: sev.color }}>{sev.label} · {detail.category}</span>
                      <span className="px-2 py-1 text-[10px] tracking-[0.08em] uppercase"
                        style={{ backgroundColor: `${status.color}18`, color: status.color }}>{status.label}</span>
                      {detail.count > 1 && (
                        <span className="px-2 py-1 text-[10px]" style={{ backgroundColor: '#F0F0F0', color: '#595959' }}>×{detail.count} occurrences</span>
                      )}
                    </div>

                    {/* Message */}
                    <div className="p-3 border" style={{ borderColor: '#E5E5E5' }}>
                      <p className="text-[9px] tracking-[0.12em] uppercase mb-1" style={{ color: '#595959' }}>Message</p>
                      <p className="text-[12px] leading-relaxed" style={{ color: '#0F0F0F' }}>{detail.message}</p>
                    </div>

                    {/* Timestamps */}
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>First Seen</p>
                        <p style={{ color: '#0F0F0F' }}>{new Date(detail.first_seen).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>Last Seen</p>
                        <p style={{ color: '#0F0F0F' }}>{new Date(detail.last_seen).toLocaleString('en-IN')}</p>
                      </div>
                    </div>

                    {/* Related entities */}
                    {(detail.user || detail.tailor || detail.order) && (
                      <div>
                        <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#595959' }}>Related</p>
                        <div className="space-y-1.5">
                          {detail.user && (
                            <div className="flex items-center gap-2 p-2 border" style={{ borderColor: '#E5E5E5' }}>
                              <User className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                              <div className="min-w-0">
                                <p className="text-[11px]" style={{ color: '#0F0F0F' }}>{detail.user.name} <span className="text-[9px] uppercase tracking-[0.05em]" style={{ color: '#595959' }}>· {detail.user.role}</span></p>
                                <p className="text-[10px] truncate" style={{ color: '#595959' }}>{detail.user.email || detail.user.phone}</p>
                              </div>
                            </div>
                          )}
                          {detail.tailor && (
                            <div className="flex items-center gap-2 p-2 border" style={{ borderColor: '#E5E5E5' }}>
                              <Scissors className="w-3.5 h-3.5" style={{ color: '#E5B2B9' }} strokeWidth={1.5} />
                              <div className="min-w-0">
                                <p className="text-[11px]" style={{ color: '#0F0F0F' }}>{detail.tailor.name}</p>
                                <p className="text-[10px]" style={{ color: '#595959' }}>{detail.tailor.city}</p>
                              </div>
                            </div>
                          )}
                          {detail.order && (
                            <div className="flex items-center gap-2 p-2 border" style={{ borderColor: '#E5E5E5' }}>
                              <Package className="w-3.5 h-3.5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
                              <div className="min-w-0">
                                <p className="text-[11px]" style={{ color: '#0F0F0F' }}>{detail.order.order_id} · {detail.order.product}</p>
                                <p className="text-[10px]" style={{ color: '#595959' }}>{detail.order.status} · {'\u20B9'}{(detail.order.total_price || 0).toLocaleString('en-IN')}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Stack trace */}
                    {detail.stack_trace && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>
                            <Terminal className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Stack Trace
                          </p>
                          <button data-testid="err-copy-stack"
                            onClick={() => { navigator.clipboard.writeText(detail.stack_trace); }}
                            className="text-[9px] tracking-[0.05em] uppercase" style={{ color: '#D4AF37' }}>
                            <Copy className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Copy
                          </button>
                        </div>
                        <pre className="text-[9px] leading-relaxed overflow-x-auto p-3 font-mono max-h-64"
                          style={{ backgroundColor: '#0F0F0F', color: '#E5B2B9' }}>
{detail.stack_trace}
                        </pre>
                      </div>
                    )}

                    {/* Resolution history */}
                    {detail.resolution_history && detail.resolution_history.length > 0 && (
                      <div>
                        <p className="text-[10px] tracking-[0.12em] uppercase mb-1.5" style={{ color: '#595959' }}>Resolution History</p>
                        <div className="space-y-1">
                          {detail.resolution_history.map((h, i) => (
                            <div key={i} className="flex items-center gap-2 text-[10px]" style={{ color: '#0F0F0F' }}>
                              <ChevronsRight className="w-3 h-3" strokeWidth={1.5} style={{ color: '#D4AF37' }} />
                              <span className="uppercase tracking-[0.05em]">{h.status}</span>
                              <span style={{ color: '#595959' }}>· {h.at ? new Date(h.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                              {h.note && <span className="truncate" style={{ color: '#595959' }}>· {h.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Footer status actions */}
            {detail && (
              <div className="px-5 py-3 border-t flex gap-1.5" style={{ borderColor: '#E5E5E5', backgroundColor: '#FAF9F6' }}>
                {['open', 'investigating', 'resolved'].map(s => {
                  const active = detail.status === s;
                  const meta = STATUS_META[s];
                  return (
                    <button
                      key={s}
                      data-testid={`err-set-${s}`}
                      disabled={active || updatingStatus}
                      onClick={() => changeStatus(s)}
                      className="flex-1 py-2 text-[10px] tracking-[0.1em] uppercase border disabled:opacity-50"
                      style={{
                        borderColor: meta.color,
                        color: active ? '#FFF' : meta.color,
                        backgroundColor: active ? meta.color : '#FFF',
                      }}>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrors;
